import SearchInput from "@/components/common/search-input";
import { cn } from "@/utils/theme";
import { Avatar, AvatarFallback, Icon } from "@elaniintc/goes-design-system";
import { useMemo, useState, useRef, useEffect } from "react";
import { DropdownMenuSeparator } from "@radix-ui/react-dropdown-menu";
import type { UserGroup } from "./types";

interface Props {
  label: string;
  isDisabled?: boolean;
  name?: string;
  required?: boolean;
  onSelectUser: (user: UserGroup) => void;
  placeholder?: string;
  labelClassName?: string;
  userOptions: UserGroup[];
}

export const UserSearchSelector = ({
  label,
  isDisabled = false,
  name = "user-search-selector",
  required = true,
  onSelectUser,
  placeholder = "Buscar usuario...",
  labelClassName = "text-sm",
  userOptions = [],
}: Props) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const _data = useMemo<UserGroup[]>(() => {
    if (!userOptions?.length) return [];

    const q = searchQuery.trim().toLowerCase();

    // Sin búsqueda → retornar el array tal cual
    if (!q) return userOptions;

    const options = userOptions.filter((user) => {
      const fullName = `${user.name} ${user.last_name ?? ""}`.toLowerCase();
      const email = (user.email ?? "").toLowerCase();

      return fullName.includes(q) || email.includes(q);
    });

    return options;
  }, [userOptions, searchQuery]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        event.target instanceof Node &&
        !dropdownRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSelectUser = (user: UserGroup) => {
    onSelectUser(user);
    setSearchQuery("");
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <label
        className={cn(
          "font-semibold text-body-loud leading-2xl",
          labelClassName,
          isDisabled && "text-on-disabled!",
        )}
        htmlFor={name}
      >
        {label}{" "}
        {required && (
          <span
            className={cn(
              "text-sm text-danger",
              isDisabled && "text-on-disabled!",
            )}
          >
            *
          </span>
        )}
      </label>
      <div className="relative">
        <SearchInput
          ref={inputRef}
          className="w-full text-size-body!"
          variant="md"
          placeholder={placeholder}
          value={searchQuery}
          onChange={(e) => {
            setSearchQuery(e.target.value);
            if (!isOpen) {
              setIsOpen(true);
            }
          }}
          onFocus={() => {
            setIsOpen(true);
          }}
        />
      </div>

      {isOpen && (
        <div className="absolute z-50 w-full mt-1 rounded-lg border border-neutral-silent text-body-loud font-semibold bg-neutral-object shadow-lg">
          <div className="max-h-55 overflow-auto pt-2">
            <Content
              _data={_data}
              onSelectUser={handleSelectUser}
              isEmpty={_data.length === 0}
            />
          </div>
        </div>
      )}
    </div>
  );
};

const Content = ({
  _data,
  onSelectUser,
  isEmpty,
}: {
  _data: UserGroup[];
  onSelectUser: (user: UserGroup) => void;
  isEmpty: boolean;
}) => {
  if (isEmpty) {
    return (
      <div className="w-full h-52.5 flex items-center justify-center">
        <span className="text-body-quiet font-normal">
          No hay más resultados
        </span>
      </div>
    );
  }

  return (
    <ul className="px-2 min-h-32">
      {_data?.map((group, index) => (
        <div key={`${group.group_id}--${index}`}>
          <div className="flex items-center gap-2 px-3 py-1">
            <span className="text-xs text-body-quiet font-semibold  tracking-wide">
              <Icon type="fa" name="users" className="text-current mr-1" />{" "}
              {group.group_name}
            </span>
            <DropdownMenuSeparator className="border-neutral-quiet border-t flex-1" />
          </div>

          <li>
            <button
              type="button"
              className="flex items-center justify-between gap-3 p-2 w-full hover:bg-neutral-object-accent-quiet rounded-lg cursor-pointer"
              onClick={() => onSelectUser(group)}
            >
              <Avatar size="2xs">
                <AvatarFallback color="action" className="pointer-events-none">
                  {group.name.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <div className="flex-1 flex items-center gap-2 text-base leading-3xl truncate pointer-events-none select-none">
                <span className="text-body font-semibold whitespace-nowrap">{`${group.name} ${group.last_name}`}</span>
                <span className="text-body-quiet font-normal truncate">
                  {group.email || "-"}
                </span>
              </div>

              <div className="pl-3">
                <Icon
                  type="far"
                  name="plus"
                  className="text-current text-base leading-3xl pointer-events-none"
                />
              </div>
            </button>
          </li>
        </div>
      ))}
    </ul>
  );
};
